import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Col, Row } from 'react-bootstrap';
//api
import { deleteDetailOrder, getAllOrdersNotPaginated, updateArticleStatus } from 'api/orders';
import { getAllOrders } from 'api/pathForHook/orders';
//stores
import { useUserStore } from 'stores';
//helpers
import { formatDateToSpanishWithTime } from 'helpers';
//utils
import { createFilesToExport } from 'utils/backoffice/exportPDFExcel';
import useHandleSearch from 'utils/customHooks/useHandleSearch';
import { formatCurrency } from 'utils/currencyFormatter';
//components
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import CustomTable from 'components/backoffice/customTable';
import DropdownIcons from 'components/backoffice/dropDownIcons';
import Title from 'components/backoffice/texts/title';
import Button from 'components/backoffice/buttonCustom';
import StatusBudget from 'components/budget/StatusBudget';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import FiltersTableOrders from './FiltersTableOrders';
import CustomCheckbox from 'components/backoffice/customCheckbox';

const Purchases = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const { profile, benefitedBookstore } = useUserStore();
  const [listOrders, setListOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [responseInfo, setResponseInfo] = useState({});
  const { t } = useTranslation(['backoffice','contact']);
  const { setTitle } = useOutletContext();
  const [order, setOrder] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  const { fetchData, stateData, loading } = useHandleSearch();
  const [formFilter, setFormFilter] = useState({
    code: '',
    usertype: '',
    benefited_company: null,
    order_status: '',
    search: '',
  });

  const toggleSelectAll = useCallback(async (e) => {
    if (e.target.checked) {
      const { data, status } = await getAllOrdersNotPaginated();
      if (status === 200) {
        setSelectedRows(data.map((row) => row)); 
        setAllOrders(data); 
      }
    } else {
      setSelectedRows([]); 
      setAllOrders([]);
    }
  }, []);

  const toggleSelectRow = (row) => {
    setAllOrders([]);
    setSelectedRows(
      (prev) =>
        prev.some((el) => el.id === row.id)
          ? prev.filter((el) => el.id !== row.id) 
          : [...prev, row], 
    );
  };

  const colsOrders = useMemo(() => [
    <CustomCheckbox
      key="select-all-checkbox"
      onChange={toggleSelectAll}
      checked={allOrders.length > 0 || ((selectedRows?.length || 0) === (listOrders?.length || 0) && (listOrders?.length || 0) > 0)}
    />,
    t('order'),
    t('user'),
    /* t('benefitedbookstore'), */
    t('date'),
    t('amount'),
    t('state'),
    t('productosTotales'),
    t('contact', { ns: 'contact' }),
    t('actions'),
  ], [allOrders.length, selectedRows?.length, listOrders?.length, toggleSelectAll, t]);

  const getListOrders = async () => {
    try {
      const searchBenefitedCompany = profile === 'library' && benefitedBookstore?.label
        ? benefitedBookstore.label
        : formFilter.benefited_company?.fiscal_name || formFilter.benefited_company?.name || '';
      
      const payload = {
        page,
        page_size: 10,
        code: formFilter.code || '',
        user_type: formFilter.usertype || '',
        search_benefited_company: searchBenefitedCompany,
        order_status: formFilter.order_status || '',
        search: formFilter.search || '',
        fields: 'id,code,user_email,library,benefited_company,fecha,total,status,contact_info,empresa,articles,user,shipping_address,total_order',
      };
      await fetchData(getAllOrders(), payload);
    } catch (error) {
      console.error(error);
    }
  };

  const serializerStatusOrder = (status) => {
    if (['received_customer'].includes(status)) {
      return 'success';
    } else if (['shipped'].includes(status)) {
      return 'success-light';
    } else if (['pending', 'incomplete'].includes(status)) {
      return 'warning';
    } else if (['cancelled', 'returned'].includes(status)) {
      return 'danger';
    }
    return 'info';
  };

  useEffect(() => {
    setTitle(t('pedidos'));
    return () => {
      setTitle('');
    };
  }, [setTitle]);

  useEffect(() => {
    if (profile === 'library' && benefitedBookstore?.label && !formFilter.benefited_company) {
      setFormFilter((prev) => ({
        ...prev,
        benefited_company: {
          id: benefitedBookstore.value,
          name: benefitedBookstore.label,
          fiscal_name: benefitedBookstore.label,
        },
      }));
    }
  }, [profile, benefitedBookstore]);

  useEffect(() => {
    getListOrders();
  }, [page, formFilter, profile, benefitedBookstore]);

  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setListOrders(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setListOrders([]);
      setResponseInfo({ errors });
    }
  }, [stateData]);

  const handleClear = () => {
    const clearedFilter = {
      code: '',
      usertype: '',
      order_status: '',
      search: '',
    };
    
    if (profile === 'library' && benefitedBookstore?.label) {
      clearedFilter.benefited_company = {
        id: benefitedBookstore.value,
        name: benefitedBookstore.label,
        fiscal_name: benefitedBookstore.label,
      };
    } else {
      clearedFilter.benefited_company = null;
    }
    
    setFormFilter(clearedFilter);
    setPage(1);
    setResponseInfo((prevResponseInfo) => ({ ...prevResponseInfo, noResults: false }));
    setResponseInfo((prevResponseInfo) => ({ ...prevResponseInfo, totalPages: responseInfo?.totalPages }));
  };

  const handleExportOrders = () => {
    const selectedData = allOrders.length > 0 ? allOrders : selectedRows;
    createFilesToExport(t, selectedData, 'xlsx', 'order');
    setSelectedRows([]);
  };

  const handleImportOrders = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { status, data } = await updateArticleStatus(formData);
      if (status === 200 || status === 201) {
        let message = '';
        if (data instanceof Blob) {
          try {
            const text = await data.text();
            message = text.trim();
          } catch (error) {
            console.error('Error al leer la respuesta:', error);
          }
        } else if (typeof data === 'string') {
          message = data;
        } else if (data?.message || data?.detail) {
          message = data.message || data.detail;
        }

        if (message) {
          if (message.toLowerCase().includes('successfully') || message.toLowerCase().includes('executed successfully')) {
            toast.success(t('importadoCorrectamente'));
          } else if (message.toLowerCase().includes('no article found') || message.toLowerCase().includes('quantity updated: 0')) {
            toast.warning(t('revisarDatosImportacion'), {
              autoClose: 6000,
            });
          } else {
            toast.info(message, {
              autoClose: 6000,
            });
          }
        } else {
          toast.success(t('importadoCorrectamente'));
        }
        getListOrders();
      } else {
        toast.error(t('errorImportar') || `Error al importar: ${status}`);
      }
    } catch (error) {
      console.error('Error al importar:', error);
      toast.error(t('errorImportar') || 'Error al importar el archivo');
    } finally {
      setIsImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const openModalDelete = (object) => {
    setShowDeleteModal(true);
    setOrder(object);
  };

  const onDelete = async () => {
    const { status } = await deleteDetailOrder(order.id);
    if (status === 204) {
      toast.success(t('eliminadocorrectamente'));
      setFormFilter({
        code: '',
        usertype: '',
        search: '',
      });
      setShowDeleteModal(false);
    } else {
      toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
    }
  };

  const handleEdit = (id) => navigate(`/intranet/purchases/${id}/edit`);
  const handleDelete = (order) => openModalDelete(order);

  return (
    <div className="d-lg-grid">
      <Title text={t('pedidos')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col xl={10} lg={12} md={12} xs={12} className="d-flex text-start">
          <FiltersTableOrders
            formFilter={formFilter}
            setFormFilter={setFormFilter}
            handleClear={handleClear}
            t={t}
            page={page}
            setPage={setPage}
          />
        </Col>
        <Col
          xl={2}
          lg={12}
          md={12}
          xs={12}
          className="d-flex text-end align-bottom p-xl-0 justify-content-end pt-5 pt-xl-0 gap-2"
        >
          {userPermissions.includes('pedido.__add') && (
            <>
              {profile !== 'library' && (
                <Button
                  variant="secondary"
                  color="primary"
                  onClick={() => {
                    navigate('/intranet/purchases/create');
                  }}
                >
                  {t('crear')}
                </Button>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportOrders}
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
              />
              <Button
                variant="secondary"
                color="primary"
                onClick={() => fileInputRef.current?.click()}
                disabled={isImporting}
              >
                {t('importar')}
              </Button>
              <Button
                variant="secondary"
                color="primary"
                onClick={handleExportOrders}
                disabled={selectedRows.length === 0}
              >
                {t('export')}
              </Button>
            </>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsOrders}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {listOrders &&
            listOrders.map((order) => (
              <tr key={order.id} data-id={order.id}>
                <td>
                  <CustomCheckbox
                    checked={selectedRows.some((el) => el.id === order.id)}
                    onChange={() => toggleSelectRow(order)}
                  />
                </td>
                <td>{order.code}</td>
                <td>{order.user_email}</td>
               {/*  <td>{order.benefited_company?.name || '-'}</td> */}
                <td>{formatDateToSpanishWithTime(order.fecha)}</td>
                <td className="text-end">{formatCurrency(order.total_order)}</td>
                <td>
                  <div className="d-flex justify-content-center">
                    <StatusBudget text={t(`orderTrans.${order.status?.text}`)} status={serializerStatusOrder(order.status?.id)} />
                  </div>
                </td>
                <td className="text-center">
                  {Array.isArray(order.articles)
                    ? order.articles.reduce((total, article) => total + Number(article.quantity || 0), 0)
                    : 0}
                </td>
                <td>
                  <div className="d-flex flex-column align-items-start justify-content-center gap-1">
                    {order.contact_info?.name ? <span>{order.contact_info.name}</span> : '-'}
                    {order.contact_info?.email ? <span>{order.contact_info.email}</span> : '-'}
                  </div>
                </td>
                <td className="item-dropdown text-end">
                  <DropdownIcons
                    idItem={order.id}
                    handleEdit={userPermissions.includes('pedido.__modify') && (() => handleEdit(order.id))}
                    handleDelete={userPermissions.includes('pedido.__delete') && (() => handleDelete(order))}
                  />
                </td>
              </tr>
            ))}
        </CustomTable>
      </div>
      <DeleteModalCustom
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        handleDelete={onDelete}
        deleteTitle={t('deleteorder')}
        beforeTitle={t('pedidos')}
      />
    </div>
  );
};

export default Purchases;
